import z from 'zod';
import {
  AllCharLabelSchema,
  CognitoUserIdSchema,
  UUIDSchema,
  UnixTimestampSchema,
} from '../common/schemas';
import {
  DBJsonObjectSchema,
  DBSha256Schema,
  DBTimestampSchema,
  JsonObjectSchema,
  Sha256Schema,
} from '../common/sql';
import { SOURCE_DOCUMENT_STATUS_DETAIL_MAX_LENGTH } from '../sourceDocuments/schemas';
import {
  FIRST_DIGITISATION_RUNG,
  LAST_DIGITISATION_RUNG,
  QUESTION_DIGITISATION_RUN_NOTE_MAX_LENGTH,
} from './constants';
import { QuestionDigitisationRunTrigger, QuestionDigitisationRunStatus } from './enums';

/**
 * DB Schemas
 */

export const DBQuestionDigitisationRunIdSchema = UUIDSchema;
export const DBQuestionDigitisationRunAttemptSchema = z.number().int().positive();
export const DBQuestionDigitisationRunRungSchema = z
  .number()
  .int()
  .min(FIRST_DIGITISATION_RUNG)
  .max(LAST_DIGITISATION_RUNG)
  .nullable();
export const DBQuestionDigitisationRunTriggerSchema = z.enum(QuestionDigitisationRunTrigger);
export const DBQuestionDigitisationRunNoteSchema = AllCharLabelSchema.min(1)
  .max(QUESTION_DIGITISATION_RUN_NOTE_MAX_LENGTH)
  .nullable();
export const DBQuestionDigitisationRunStatusSchema = z.enum(QuestionDigitisationRunStatus);
export const DBQuestionDigitisationRunModelSchema = z.string().min(1).max(128).nullable();
export const DBQuestionDigitisationRunStatusDetailSchema = AllCharLabelSchema.min(1)
  .max(SOURCE_DOCUMENT_STATUS_DETAIL_MAX_LENGTH)
  .nullable();
export const DBQuestionDigitisationRunCountSchema = z.number().int().min(0);

/** A fault as a check reports it: its code, where in the tree, and its own wording. */
export const DBQuestionDigitisationRunFaultSchema = z.object({
  code: z.string().min(1).max(64),
  path: z.string().min(1),
  message: z.string().min(1),
});

export const DBQuestionDigitisationRunSchema = z.object({
  id: DBQuestionDigitisationRunIdSchema,
  documentQuestionId: UUIDSchema,
  attempt: DBQuestionDigitisationRunAttemptSchema,
  rung: DBQuestionDigitisationRunRungSchema,
  trigger: DBQuestionDigitisationRunTriggerSchema,
  requestedBy: CognitoUserIdSchema.nullable(),
  note: DBQuestionDigitisationRunNoteSchema,
  model: DBQuestionDigitisationRunModelSchema,
  config: DBJsonObjectSchema,
  configHash: DBSha256Schema,
  status: DBQuestionDigitisationRunStatusSchema,
  statusDetail: DBQuestionDigitisationRunStatusDetailSchema,
  reclaims: DBQuestionDigitisationRunCountSchema,
  faults: z.array(DBQuestionDigitisationRunFaultSchema),
  stats: DBJsonObjectSchema.nullable(),
  replacedRootQuestionIds: z.array(UUIDSchema),
  createdRootQuestionIds: z.array(UUIDSchema),
  overridesCarried: DBQuestionDigitisationRunCountSchema,
  overridesDropped: DBQuestionDigitisationRunCountSchema,
  startedAt: DBTimestampSchema.nullable(),
  finishedAt: DBTimestampSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

/**
 * Client Schemas
 */

export const QuestionDigitisationRunIdSchema = DBQuestionDigitisationRunIdSchema;
export const QuestionDigitisationRunAttemptSchema = DBQuestionDigitisationRunAttemptSchema.openapi({
  description: 'Counts every attempt at digitising the printed question, from 1',
  example: 2,
});
export const QuestionDigitisationRunRungSchema = DBQuestionDigitisationRunRungSchema.openapi({
  description: "The engine's place on the retry ladder, 1 to 3. Null for a person's run.",
  example: 2,
});
export const QuestionDigitisationRunTriggerSchema = DBQuestionDigitisationRunTriggerSchema.openapi({
  description: 'What opened the run: the survey, the engine retrying, or a person',
  example: QuestionDigitisationRunTrigger.Retry,
});
export const QuestionDigitisationRunNoteSchema = DBQuestionDigitisationRunNoteSchema.openapi({
  description: 'What the person who asked for the run wrote for the model',
  example: 'Part (b) continues onto page 7.',
});
export const QuestionDigitisationRunModelSchema = DBQuestionDigitisationRunModelSchema.openapi({
  description: 'The resolved model selector the run used',
  example: 'openai:gpt-5.6-sol',
});
export const QuestionDigitisationRunStatusSchema = DBQuestionDigitisationRunStatusSchema.openapi({
  description: 'Processing status of the run',
  example: QuestionDigitisationRunStatus.Processed,
});
export const QuestionDigitisationRunStatusDetailSchema =
  DBQuestionDigitisationRunStatusDetailSchema;
export const QuestionDigitisationRunConfigHashSchema = Sha256Schema;
export const QuestionDigitisationRunCountSchema = DBQuestionDigitisationRunCountSchema;
export const QuestionDigitisationRunFaultSchema = DBQuestionDigitisationRunFaultSchema.extend({
  code: DBQuestionDigitisationRunFaultSchema.shape.code.openapi({
    description: 'The fault code the check reported',
    example: 'marks_do_not_sum',
  }),
  path: DBQuestionDigitisationRunFaultSchema.shape.path.openapi({
    description: "Where in the tree, in the paper's own labels",
    example: '12(b)(ii)',
  }),
  message: DBQuestionDigitisationRunFaultSchema.shape.message.openapi({
    description: "The check's own wording",
    example: "12(b)(ii)'s criteria add up to 3 marks, and the part is worth 2",
  }),
});

export const QuestionDigitisationRunSchema = z.object({
  id: QuestionDigitisationRunIdSchema,
  documentQuestionId: UUIDSchema,
  attempt: QuestionDigitisationRunAttemptSchema,
  rung: QuestionDigitisationRunRungSchema,
  trigger: QuestionDigitisationRunTriggerSchema,
  requestedBy: CognitoUserIdSchema.nullable(),
  note: QuestionDigitisationRunNoteSchema,
  model: QuestionDigitisationRunModelSchema,
  config: JsonObjectSchema,
  configHash: QuestionDigitisationRunConfigHashSchema,
  status: QuestionDigitisationRunStatusSchema,
  statusDetail: QuestionDigitisationRunStatusDetailSchema,
  reclaims: QuestionDigitisationRunCountSchema,
  faults: z.array(QuestionDigitisationRunFaultSchema),
  stats: JsonObjectSchema.nullable(),
  replacedRootQuestionIds: z.array(UUIDSchema),
  createdRootQuestionIds: z.array(UUIDSchema),
  overridesCarried: QuestionDigitisationRunCountSchema,
  overridesDropped: QuestionDigitisationRunCountSchema,
  startedAt: UnixTimestampSchema.nullable(),
  finishedAt: UnixTimestampSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});
